// Utility Helper Functions
import { TRANSCRIPTION_CONFIG, FILE_UPLOAD_CONFIG } from './constants';

/**
 * Format timestamp to readable time string with error handling
 * Handles Date objects, ISO strings, and timestamps
 */
export const formatTime = (timestamp: Date | string | number | undefined): string => {
  try {
    if (!timestamp) {
      return 'Unknown time';
    }
    
    // Convert to Date object
    const date = timestamp instanceof Date ? timestamp : new Date(timestamp);
    
    // Check if date is valid
    if (isNaN(date.getTime())) {
      return 'Invalid time';
    }
    
    return new Intl.DateTimeFormat('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    }).format(date);
  } catch (error) {
    console.error('Error formatting time:', error, 'for timestamp:', timestamp);
    return 'Time error';
  }
};

/**
 * Format date to readable date string
 */
export const formatDate = (date: Date): string => {
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  }).format(date);
};

/**
 * Format duration in milliseconds to readable string
 */
export const formatDuration = (milliseconds: number): string => {
  const seconds = Math.floor(milliseconds / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);

  if (hours > 0) {
    return `${hours}h ${minutes % 60}m ${seconds % 60}s`;
  } else if (minutes > 0) {
    return `${minutes}m ${seconds % 60}s`;
  } else {
    return `${seconds}s`;
  }
};

/**
 * Detect if text contains question indicators
 */
export const isQuestion = (text: string): boolean => {
  const lowerText = text.toLowerCase().trim();
  
  // Check for question mark
  if (lowerText.endsWith('?')) {
    return true;
  }

  // Check for question keywords
  const hasQuestionKeyword = TRANSCRIPTION_CONFIG.questionDetectionKeywords.some(keyword =>
    lowerText.startsWith(keyword.toLowerCase())
  );

  return hasQuestionKeyword;
};

/**
 * Calculate confidence color class based on score
 */
export const getConfidenceColor = (confidence: number): string => {
  if (confidence >= 80) return 'text-green-600 bg-green-100';
  if (confidence >= 60) return 'text-yellow-600 bg-yellow-100';
  return 'text-red-600 bg-red-100';
};

/**
 * Validate file upload
 */
export const validateFileUpload = (file: File): { isValid: boolean; error?: string } => {
  // Check file size
  if (file.size > FILE_UPLOAD_CONFIG.maxSize) {
    return {
      isValid: false,
      error: `File size must be less than ${FILE_UPLOAD_CONFIG.maxSize / (1024 * 1024)}MB`
    };
  }

  // Check file type
  const acceptedTypes = Object.keys(FILE_UPLOAD_CONFIG.acceptedTypes);
  if (!acceptedTypes.includes(file.type)) {
    return {
      isValid: false,
      error: 'File type not supported. Please upload PDF, DOC, DOCX, or TXT files.'
    };
  }

  return { isValid: true };
};

/**
 * Truncate text to specified length
 */
export const truncateText = (text: string, maxLength: number = 100): string => {
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength).trim() + '...';
};

/**
 * Generate unique ID
 */
export const generateId = (prefix: string = 'id'): string => {
  return `${prefix}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

/**
 * Debounce function
 */
export const debounce = <T extends (...args: any[]) => any>(
  func: T,
  delay: number
): ((...args: Parameters<T>) => void) => {
  let timeoutId: NodeJS.Timeout;
  
  return (...args: Parameters<T>) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => func(...args), delay);
  };
};

/**
 * Throttle function
 */
export const throttle = <T extends (...args: any[]) => any>(
  func: T,
  delay: number
): ((...args: Parameters<T>) => void) => {
  let lastCall = 0;
  
  return (...args: Parameters<T>) => {
    const now = Date.now();
    if (now - lastCall >= delay) {
      lastCall = now;
      func(...args);
    }
  };
};

/**
 * Deep clone object
 */
export const deepClone = <T>(obj: T): T => {
  if (obj === null || typeof obj !== 'object') return obj;
  if (obj instanceof Date) return new Date(obj.getTime()) as unknown as T;
  if (obj instanceof Array) return obj.map(item => deepClone(item)) as unknown as T;
  
  const cloned = {} as T;
  for (const key in obj) {
    if (obj.hasOwnProperty(key)) {
      cloned[key] = deepClone(obj[key]);
    }
  }
  return cloned;
};

/**
 * Check if browser supports required features
 */
export const checkBrowserSupport = (): {
  isSupported: boolean;
  missingFeatures: string[];
} => {
  const missingFeatures: string[] = [];

  if (!navigator.mediaDevices) {
    missingFeatures.push('MediaDevices API');
  }

  if (!navigator.mediaDevices?.getDisplayMedia) {
    missingFeatures.push('Screen Capture API');
  }

  if (!window.MediaRecorder) {
    missingFeatures.push('MediaRecorder API');
  }

  if (!window.AudioContext && !(window as any).webkitAudioContext) {
    missingFeatures.push('Web Audio API');
  }

  if (!window.WebSocket) {
    missingFeatures.push('WebSocket API');
  }

  return {
    isSupported: missingFeatures.length === 0,
    missingFeatures
  };
};

/**
 * Extract text content from various file types
 */
export const extractTextFromFile = async (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    
    reader.onload = (e) => {
      const text = e.target?.result as string;
      resolve(text);
    };
    
    reader.onerror = () => {
      reject(new Error('Failed to read file'));
    };
    
    if (file.type === 'application/pdf') {
      reject(new Error('PDF parsing requires additional library. Please use text format or implement PDF.js'));
    } else {
      reader.readAsText(file);
    }
  });
};

/**
 * Calculate word count
 */
export const getWordCount = (text: string): number => {
  return text.trim().split(/\s+/).filter(word => word.length > 0).length;
};

/**
 * Sanitize HTML content
 */
export const sanitizeHtml = (html: string): string => {
  const div = document.createElement('div');
  div.textContent = html;
  return div.innerHTML;
};

/**
 * Copy text to clipboard
 */
export const copyToClipboard = async (text: string): Promise<boolean> => {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch (err) {
    // Fallback for older browsers
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'fixed';
    textArea.style.left = '-999999px';
    textArea.style.top = '-999999px';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    
    try {
      document.execCommand('copy');
      document.body.removeChild(textArea);
      return true;
    } catch (err) {
      document.body.removeChild(textArea);
      return false;
    }
  }
};

/**
 * Format file size
 */
export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

/**
 * Validate email format
 */
export const isValidEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

/**
 * Generate random color
 */
export const generateRandomColor = (): string => {
  const colors = [
    'bg-red-100 text-red-800',
    'bg-blue-100 text-blue-800',
    'bg-green-100 text-green-800',
    'bg-yellow-100 text-yellow-800',
    'bg-purple-100 text-purple-800',
    'bg-pink-100 text-pink-800',
    'bg-indigo-100 text-indigo-800'
  ];
  
  return colors[Math.floor(Math.random() * colors.length)];
};
